import { AddOutlined } from "@mui/icons-material";
import { Box, Button, ListSubheader, MenuItem, Stack } from "@mui/material";
import { fillRouteWithSlugs } from "@utils";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageHeader } from "src/components";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import {
  TrainingMatrix,
  TrainingMatrixView,
} from "src/components/Trainings/TrainingMatrix";
import { downloadTrainingMatrixCsv } from "src/components/Trainings/training-matrix-export";
import { buildTrainingUserMatrixRows } from "src/components/Trainings/training-user-matrix";
import { CompactSelect } from "src/components/ui/CompactSelect";
import { StatCard } from "src/components/ui/StatCard";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import {
  useGetOrgTasks,
  useGetTrainingMatrixDocuments,
  useGetUser,
  useGetUsersByOrg,
} from "src/hooks";
import { useGetFunctionalGroups } from "src/hooks/queries";
import { useGetTaskPrograms } from "src/hooks/queries/task-program-mutations";
import { TrainingScope } from "src/services/backend";
import { EntitlementType } from "src/stores/models";
import { hasFunctionalGroupEntitlement } from "src/utils/entitlements";

const TRAINING_SCOPE_SEARCH_PARAM = "scope";
const isTrainingScope = (value: string | null): value is TrainingScope =>
  value === "mine" || value === "all";

export const TrainingPrograms = () => {
  const { orgId = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: user } = useGetUser();
  const canAdministerAllTrainings =
    !!user?.permissions?.includes("write:configuration") ||
    hasFunctionalGroupEntitlement(
      user,
      orgId,
      EntitlementType.ADMINISTER_ALL_TRAININGS
    );

  const scopeParam = searchParams.get(TRAINING_SCOPE_SEARCH_PARAM);
  const effectiveScope: TrainingScope = canAdministerAllTrainings
    ? isTrainingScope(scopeParam)
      ? scopeParam
      : "all"
    : "mine";

  const { data: programs = [], isLoading } = useGetTaskPrograms(
    orgId,
    effectiveScope
  );
  const [preferredMatrixView, setPreferredMatrixView] =
    useState<TrainingMatrixView>("user");
  const canViewByUser = canAdministerAllTrainings && effectiveScope === "all";
  const matrixView = canViewByUser ? preferredMatrixView : "program";
  const shouldLoadUserMatrix = canViewByUser;
  const {
    data: trainingTasks = [],
    isLoading: trainingTasksLoading,
    isError: trainingTasksError,
  } = useGetOrgTasks({
    orgId,
    scope: "all",
    enabled: shouldLoadUserMatrix,
  });
  const { data: functionalGroups = [] } = useGetFunctionalGroups(orgId);
  const {
    data: orgUsers = [],
    isLoading: orgUsersLoading,
    isError: orgUsersError,
  } = useGetUsersByOrg({
    orgId,
    enabled: shouldLoadUserMatrix,
  });
  const matrixDocumentIds =
    shouldLoadUserMatrix && !isLoading && !trainingTasksLoading
      ? [
          ...programs.flatMap(({ linkedDocumentId, quizzes }) =>
            quizzes.some(({ publishedAt }) => !publishedAt)
              ? [linkedDocumentId]
              : []
          ),
          ...trainingTasks.flatMap(({ taskType, documentId }) =>
            taskType === "TRAINING" && documentId ? [documentId] : []
          ),
        ]
      : [];
  const {
    data: matrixDocuments,
    isLoading: matrixDocumentsLoading,
    isError: matrixDocumentsError,
  } = useGetTrainingMatrixDocuments(orgId, matrixDocumentIds);
  const userMatrixRows = useMemo(
    () =>
      shouldLoadUserMatrix
        ? buildTrainingUserMatrixRows({
            tasks: trainingTasks,
            programs,
            users: orgUsers,
            documents: matrixDocuments,
          })
        : [],
    [matrixDocuments, orgUsers, programs, shouldLoadUserMatrix, trainingTasks]
  );
  useEffect(() => {
    if (!user || scopeParam === effectiveScope) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(TRAINING_SCOPE_SEARCH_PARAM, effectiveScope);
    setSearchParams(nextParams, { replace: true });
  }, [effectiveScope, scopeParam, searchParams, setSearchParams, user]);

  const sortedGroups = useMemo(
    () =>
      [...functionalGroups].sort((a, b) =>
        a.acronym.localeCompare(b.acronym, undefined, { sensitivity: "base" })
      ),
    [functionalGroups]
  );

  const stats = useMemo(() => {
    let completed = 0;
    let total = 0;
    let active = 0;
    for (const p of programs) {
      if (p.enabled) active += 1;
      if (p.latestOccurrence) {
        total += p.latestOccurrence.totalTasks;
        completed += p.latestOccurrence.completedTasks;
      }
    }
    return {
      completed,
      total,
      outstanding: Math.max(total - completed, 0),
      active,
      pct: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [programs]);

  return (
    <Box className="flex flex-col gap-4">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb items={[{ label: "Training Programs" }]} />
        }
        rightButtons={
          canAdministerAllTrainings ? (
            <CompactSelect
              value={effectiveScope}
              onChange={(nextScope) => {
                if (isTrainingScope(nextScope)) {
                  const nextParams = new URLSearchParams(searchParams);
                  nextParams.set(TRAINING_SCOPE_SEARCH_PARAM, nextScope);
                  setSearchParams(nextParams);
                }
              }}
              ariaLabel="Training Program scope"
              renderValue={() =>
                effectiveScope === "mine"
                  ? "My Training Programs"
                  : "All Training Programs"
              }
            >
              <ListSubheader>Show training programs</ListSubheader>
              <MenuItem value="mine">My Training Programs</MenuItem>
              <MenuItem value="all">All Training Programs</MenuItem>
            </CompactSelect>
          ) : undefined
        }
      />

      <Box className="py-2 px-3 flex flex-col gap-4">
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ width: "100%" }}
        >
          <StatCard
            label="Training Programs completed"
            value={`${stats.completed}`}
            secondary={` / ${stats.total}`}
            barColor="success"
            barValue={stats.pct}
          />
          <StatCard
            label="Outstanding"
            value={`${stats.outstanding}`}
            barColor="warning"
            barValue={stats.total ? (stats.outstanding / stats.total) * 100 : 0}
          />
          <StatCard
            label="Overall completion"
            value={`${stats.pct}%`}
            barColor="primary"
            barValue={stats.pct}
          />
          <StatCard
            label="Active Training Programs"
            value={`${stats.active}`}
          />
        </Stack>

        <TrainingMatrix
          view={matrixView}
          onViewChange={setPreferredMatrixView}
          programs={programs}
          functionalGroups={sortedGroups}
          programsLoading={isLoading}
          userRows={userMatrixRows}
          userRowsLoading={
            shouldLoadUserMatrix &&
            (trainingTasksLoading || orgUsersLoading || matrixDocumentsLoading)
          }
          userRowsError={
            shouldLoadUserMatrix &&
            (trainingTasksError || orgUsersError || matrixDocumentsError)
          }
          canViewByUser={canViewByUser}
          scope={effectiveScope}
          action={
            canAdministerAllTrainings && effectiveScope === "all" ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddOutlined />}
                onClick={() =>
                  navigate(
                    fillRouteWithSlugs(ROUTES.TRAINING_ASSIGN, {
                      [ROUTE_SLUGS.ORG_ID]: orgId,
                    })
                  )
                }
                sx={{
                  flexShrink: 0,
                  borderColor: "divider",
                  color: "text.primary",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderColor: "text.secondary",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                Assign Trainings
              </Button>
            ) : undefined
          }
          onExport={() => downloadTrainingMatrixCsv(userMatrixRows)}
          onSelectProgram={(programId) =>
            navigate(
              fillRouteWithSlugs(ROUTES.TRAINING_DETAIL, {
                [ROUTE_SLUGS.ORG_ID]: orgId,
                [ROUTE_SLUGS.PROGRAM_ID]: programId,
              })
            )
          }
        />
      </Box>
    </Box>
  );
};
